"use client";

import { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";

// Magic-UI-style Number Ticker — pure framer-motion (motion value + spring).
export function NumberTicker({
  value,
  direction = "up",
  delay = 0,
  className = "",
  decimalPlaces = 0,
  duration = 1600,
  formatter,
}) {
  const ref = useRef(null);
  const spanRef = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px" });

  const from = direction === "down" ? value : 0;
  const to = direction === "down" ? 0 : value;

  const mv = useMotionValue(from);
  const smooth = useSpring(mv, {
    // stiffness/damping tuned to hit target in roughly `duration` ms
    stiffness: 60,
    damping: 20,
    mass: 1,
  });
  const rounded = useTransform(smooth, (v) =>
    formatter
      ? formatter(v)
      : Number(v).toLocaleString("en-IN", {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        }),
  );

  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => mv.set(to), delay);
    return () => clearTimeout(t);
  }, [inView, to, delay, mv]);

  // paint text
  useEffect(() => {
    const unsub = rounded.on("change", (v) => {
      if (spanRef.current) spanRef.current.textContent = v;
    });
    return unsub;
  }, [rounded]);

  // seed initial paint
  useEffect(() => {
    if (spanRef.current)
      spanRef.current.textContent = Number(from).toLocaleString("en-IN", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
  }, [from, decimalPlaces]);

  return (
    <span ref={ref} className={className}>
      <span ref={spanRef} />
    </span>
  );
}

export default NumberTicker;
